import {
  VisitNoteDTO,
  PatientDTO,
  InsuranceDTO,
  MultiMediaResponse,
  VisitNoteBillingCodeDTO,
  ClaimBundleDTO,
  ClaimDTO,
  ClaimContextDTO,
  ClaimLevelAuthorizationDTO,
  ServiceFacilityDTO,
  VisitNoteBillingAssessmentCodeDTO,
  ClaimInsurancePolicySettingDTO,
  ClaimInsuranceTypeSettingDTO,
  ClaimBenefitsAssignmentSettingDTO,
  ClaimPlanParticipationSettingsDTO,
  ClaimResubmissionCodeSettingDTO,
  ClaimDateQualifierSettingDTO,
  ChargesResponseClaimDTO,
  ServiceCodeDetailsDTO,
  SplitClaimDTO,
  SplitClaimsDTO,
  ChargeFilterDTO,
  ChargeResponseServiceCodeDTO,
  ChargesResponseItemDTO,
  PatientPaymentTransactionFilterDTO,
  ChargeMasterDTO,
  CustomChargeMasterDTO,
  ChargeResponseCustomServiceCodeDTO,
  CustomServiceCodeViewDTO,
  BillingCodeViewDTO,
  EOBServiceCodePaymentInfoDTO,
  EncounterFinancialDetailsDTO,
  PatientEncounterFinancialDTO,
  PatientStatementRequestDTO,
  EOBDataForClaimViewDTO,
  UserDTO,
  BillingProviderDTO,
  PatientPaymentTransactionViewModelDTO,
  ClaimDelayReasonCodeSettingDTO,
} from 'dtos';
import {
  PatientPaymentTypes,
} from 'dtos/patientPaymentTypes';
import {
  EOBServiceLineRetirementReason,
} from 'dtos/eOBServiceLineRetirementReason';
import {
  Dayjs,
} from 'utils/dayjs';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import {
  PatientStatementDTO,
} from 'dtos/patientStatementDTO';
import {
  ClaimAdjustment,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/EOBAdjustmentCodePopover';
import {
  GetQueryParams,
} from 'pages/Dashboard/types/common';
import {
  VisitNoteBillingCode,
} from 'views/EMR/components/ServiceLookup/type';
import {
  EOBFilterDTO,
} from 'dtos/eOBFilterDTO';
import {
  AdjustmentRelatedField,
} from 'pages/Dashboard/pages/Billing/Payments/constants';
import {
  ClaimAttachmentDTO,
} from 'dtos/claimAttachmentDTO';
import {
  ClaimAttachmentTransmissionCodeSettingDTO,
} from 'dtos/claimAttachmentTransmissionCodeSettingDTO';

export type Price = Pick<ChargeMasterDTO,
  'billingCode'
  | 'chargeAmount'
  | 'createdOn'
  | 'updatedOn'>
  & Pick<CustomChargeMasterDTO, 'customServiceCode'>
  & {
    id?: number; // chargeMasterId | customChargeMasterId
    codeId?: number; // billingCodeId | customServiceCodeId
    isCustom?: boolean; // cpt: false; custom: true
  };

const priceServiceNames = ['cpt', 'custom'] as const;
export type PriceService = typeof priceServiceNames[number];

export type BillingDataParams = GetQueryParams & { filterQuery: ChargeFilterDTO };

export type PaymentDataParams = Omit<BillingDataParams, 'searchQuery'> & {
  patientSearchText?: string;
  searchText?: string;
  filterQuery?: PatientPaymentTransactionFilterDTO & EOBFilterDTO;
};

export type ExtendedServiceCode = Partial<
  Omit<VisitNoteBillingCodeDTO, 'serviceCodeDetails'>
  & Pick<ChargeResponseServiceCodeDTO,
    'allowedAmount'
    | 'visitNoteBillingCodeId'
    | 'visitNoteBaseBillId'
    | 'primaryClaimId'
    | 'secondaryClaimId'
    | 'insurancePaidAmount'
    | 'billedAmount'
    | 'numberOfUnits'>
  & Pick<ChargeResponseCustomServiceCodeDTO,
    'visitNoteBillingCustomServiceCodeId'
    | 'serviceName'>
  & {
    serviceCodeDetails: ServiceCodeDetailsDTO & Pick<ChargeResponseServiceCodeDTO,
      'allowedAmount'
      | 'insurancePaidAmount'>;
  }
>;

export type ChargesRowCommonInfo = {
  isChild?: boolean;
  isDetailsLoading?: boolean;
  rowKey?: string;
  patientId?: number;
  masterClaimId?: number;
  isOutOfPocket?: boolean;
  encounterId?: number;
};

export type ChargesClaimInfo = ChargesResponseClaimDTO & ChargesRowCommonInfo & {
  secondaryClaim?: ChargesResponseClaimDTO;
  claimIndex?: number;
  keyedSecondaryClaims?: Record<number, ChargesResponseClaimDTO>;
  isClaim?: boolean;
};

export type ChargesCodeInfo = Omit<ExtendedServiceCode, 'serviceCodeDetails'> & ChargesRowCommonInfo & {
  isCode?: boolean;
};

export type ChargeInfo = ChargesClaimInfo & ChargesCodeInfo;

export type ChargesEncounterRowData = Omit<VisitNoteDTO,
  | 'bill'
  | 'customServiceCodes'
  | 'serviceCodes'
  >
  & Pick<ChargesResponseItemDTO,
  | 'patientResponsibility'
  | 'patientBalance'
  | 'insuranceBalance'
  >
& {
  patient: PatientDTO & { profileImageUrl?: string };
  chargesInfo: Partial<ChargeInfo>[];
  claimCharges: Partial<ChargeInfo>[];
  billingState?: string | null;
  expandable?: boolean;
};

export type ChargesRowData = Partial<ChargesEncounterRowData & ChargeInfo>;

export type PatientPaymentTransaction = PatientPaymentTransactionViewModelDTO & {
  patient?: PatientCompact;
  isRefund?: boolean;
};

export type LocalMediaFile = {
  original?: File;
  media: MultiMediaResponse;
};

export type Breakdown = Partial<EOBServiceCodePaymentInfoDTO> & {
  adjustments?: ClaimAdjustment[] | null;
};

export type AdjustmentBaseAmounts = Partial<Partial<Record<AdjustmentRelatedField, number>>>;

export type ServiceAllocation =
  Partial<Breakdown>
  & Partial<CustomServiceCodeViewDTO & BillingCodeViewDTO>
  & {
  id?: number;
  isPlaceholder?: boolean;
  serviceName?: string;
  type?: PriceService;
  encounterId?: number;
  eobId?: number;
  claimId?: number;
  claimExternalId?: string;
  eobNumber?: string;
  formattedVisitDate?: string;
  patientId?: number;
  patientFullName?: string;
  reasonForVisit?: string;
  total?: number | null;
  suggestedInsuranceWriteoff?: number | null;
  adjustmentBaseAmounts?: AdjustmentBaseAmounts;
  suppressBilledAmount?: boolean;
  retirementReason?: EOBServiceLineRetirementReason;
  canManuallyExclude?: boolean;
};

export type VisitInfo = Pick<EOBDataForClaimViewDTO,
 | 'typeOfVisitDescription'
 | 'claim'
 | 'claimId'
 | 'isShiftedToPatient'
 | 'eobClaimPayments'
 > & {
  encounterId: number;
  eobNumbers?: Set<string>;
  hasEOBs?: boolean;
  visitDate?: string;
  formattedVisitDate?: string;
  patientId?: number;
  patient?: PatientCompact;
  allocation?: ServiceAllocation[];
  relatedAllocation?: ServiceAllocation[];
  unallocated?: number;
  insurancePaymentTotal?: number;
};

export type VisitNoteBillInfo = VisitInfo
  & {
  paymentId?: number;
  patientTotalResponsibility?: number;
  balance?: number;
  patientPaid?: number;
  patientCurrentPayment?: number;
  balanceWithoutCopay?: number;
};

export type ClaimAttachment = ClaimAttachmentDTO & {
  transmissionCode?: ClaimAttachmentTransmissionCodeSettingDTO;
};

export type ClaimAuthorizationSelection = Pick<ClaimLevelAuthorizationDTO, 'authorizationNumber'>;

export type Claim = Pick<ClaimBundleDTO,
  'insuranceAmountPaid' |
  'visitNote' |
  'secondaryClaimActionState'>
  & Pick<ClaimDTO,
  'claimId'
  | 'referencedPrimaryClaimId'
  | 'patientId'
  | 'encounterId'
  | 'organizationId'
  | 'externalId'
  | 'referencedPrimaryPayerSecondaryIdentifier'
  | 'notes'
  | 'updatedOn'>
  & ClaimContextDTO
  & Omit<NonNullable<ClaimDTO['details']>,
  'dates'
  | 'employment'
  | 'delayReasonCode'
  | 'authorization'
  | 'resubmissionCode'>
  & {
  patientAuthorizationSelection?: ClaimAuthorizationSelection;
  assessmentCodes?: VisitNoteBillingAssessmentCodeDTO[] | null;
  billingCodes?: VisitNoteBillingCode[] | null;
  requestType?: 'submit' | 'validate';
  isRelatedToEmployment?: boolean;
  serviceFacility?: ServiceFacilityDTO;
  billingProvider?: BillingProviderDTO;
  renderingProvider?: UserDTO;
  insurance?: InsuranceDTO;
  benefitsAssignment?: ClaimBenefitsAssignmentSettingDTO;
  planParticipation?: ClaimPlanParticipationSettingsDTO;
  insurancePolicy?: ClaimInsurancePolicySettingDTO;
  insuranceType?: ClaimInsuranceTypeSettingDTO;
  delayReasonCode?: ClaimDelayReasonCodeSettingDTO;
  resubmissionCode?: ClaimResubmissionCodeSettingDTO;
  dates?: { date: Dayjs; item: ClaimDateQualifierSettingDTO }[];
  claimAttachments?: ClaimAttachment[] | null;
};

export type EOBClaimRequest = {
  eobId: number;
  claimId: number;
};

type ClaimSplitServiceDetails = Omit<ServiceCodeDetailsDTO, 'primaryClaimId'> & {
  primaryClaimId?: string | number;
};

type ClaimSplitServiceCode = Omit<VisitNoteBillingCodeDTO, 'serviceCodeDetails'> & {
  serviceCodeDetails?: ClaimSplitServiceDetails;
};

export type ClaimSplit = Omit<SplitClaimDTO, 'claimId' | 'serviceCodes'> & {
  claimId: string;
  serviceCodes?: ClaimSplitServiceCode[];
};

export type ClaimSplitsBundle = Omit<SplitClaimsDTO, 'splitClaims'> & {
  splitClaims: ClaimSplit[] | null;
};

export type ClaimDataForm = Omit<Claim, 'requestType'>;

export type PatientInvoice = PatientStatementDTO & {
  visitNotesForStatement: {
    [key: string]: EncounterFinancialDetailsDTO & {
      totalDue: number;
      patientBalance: PatientEncounterFinancialDTO;
      allocation: ServiceAllocation[];
    };
  };
};

export type PaymentSummary = {
  encounterId?: number;
  amount?: number;
  paymentType?: PatientPaymentTypes;
};

export type PaymentStatus = {
  isComplete?: boolean;
  paymentSummaries?: PaymentSummary[];
  userId?: number;
  patientPaymentId?: number;
};

export type PatientStatementRequest = PatientStatementRequestDTO & {
  billInfo?: VisitNoteBillInfo[];
};

export const NotSetSentinal = '__NOT_SET__';

export type ExcludeServiceLineRequest = {
  eobServiceCodePaymentInfoId: number;
  isExcluded: boolean;
};

export type ClaimRowData = ChargeInfo & {
  reasonForVisit?: string;
  visitDateTime?: string;
  expandable?: boolean;
  patient?: PatientDTO;
  uniqueId?: string;
};
